/** Readable option cards with whole-card exploration and optional separate draft actions. */
import type { FC, ReactNode } from 'react';

export interface OptionCard {
  id: string;
  title: string;
  description?: string;
  /** Pre-formatted display value, e.g. "$80" — blocks never compute money. */
  value?: string;
  valuePrefix?: string;
  meta?: string;
  /** 0–4 duration ticks; omit to hide the tick row entirely. */
  metaTicks?: number;
  featured?: boolean;
  featuredLabel?: string;
  /** Optional media header, rendered edge-to-edge above the card content. */
  imageUrl?: string;
}

export interface OptionCardGridProps {
  options: OptionCard[];
  onSelect: (id: string) => void;
  selectLabel?: string;
  renderFooter?: (option: OptionCard) => ReactNode;
}

const TICK_COUNT = 4;

export const OptionCardGrid: FC<OptionCardGridProps> = ({
  options,
  onSelect,
  selectLabel,
  renderFooter,
}) => (
  <div
    className={`grid grid-cols-1 gap-3.5 ${options.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}
  >
    {options.map((option) => {
      const ticks = Math.max(0, Math.min(TICK_COUNT, option.metaTicks ?? 0));
      return (
        <div
          key={option.id}
          className="relative flex flex-col rounded-lg border border-border bg-card"
          data-featured={option.featured === true}
        >
          <button
            type="button"
            className="relative flex w-full flex-1 cursor-pointer flex-col overflow-hidden rounded-lg bg-card text-left transition-colors hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={(event) => {
              if (!event.currentTarget.ownerDocument.getSelection()?.toString()) {
                onSelect(option.id);
              }
            }}
          >
            {option.imageUrl ? (
              <img
                src={option.imageUrl}
                alt=""
                className="aspect-video w-full rounded-t-lg object-cover"
              />
            ) : null}
            <div className="flex flex-1 flex-col gap-2.5 p-3 sm:gap-3.5 sm:p-5">
              {option.value || (option.featured && option.featuredLabel) ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold tracking-tighter tabular-nums">
                    {option.valuePrefix ? (
                      <small className="mr-0.5 text-xs font-medium text-muted-foreground-subtle">
                        {option.valuePrefix}{' '}
                      </small>
                    ) : null}
                    {option.value}
                  </span>
                  {option.featured && option.featuredLabel ? (
                    <span className="text-xs font-semibold text-muted-foreground">
                      {option.featuredLabel}
                    </span>
                  ) : null}
                </div>
              ) : null}
              <div>
                <div className="text-base font-bold tracking-tight">{option.title}</div>
                {option.description ? (
                  <div className="text-sm leading-normal text-muted-foreground">
                    {option.description}
                  </div>
                ) : null}
              </div>
              {option.metaTicks !== undefined || option.meta || selectLabel ? (
                <div className="mt-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.75">
                    {option.metaTicks !== undefined ? (
                      <span className="inline-flex gap-0.5">
                        {Array.from({ length: TICK_COUNT }, (_, tickIndex) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length tick marks, no identity
                          <i
                            key={tickIndex}
                            data-on={tickIndex < ticks}
                            className="block h-1 w-2.25 rounded-sm bg-border not-italic data-[on=true]:bg-primary"
                          />
                        ))}
                      </span>
                    ) : null}
                    {option.meta ? (
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {option.meta}
                      </span>
                    ) : null}
                  </span>
                  {selectLabel ? (
                    <span className="text-sm font-medium underline underline-offset-4">
                      {selectLabel}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </button>
          {renderFooter ? <div className="px-2 pb-2">{renderFooter(option)}</div> : null}
        </div>
      );
    })}
  </div>
);
